import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, unwrap } from '../api/client';

import type { Me } from '../api/client';
import type { QueryClient } from '@tanstack/react-query';

export const ME_KEY = ['me'] as const;

/**
 * Cambia de usuario o de clínica: fija `me` y borra el resto de la caché. No usa clear(): borraría
 * también la consulta de `me` a la que está suscrito useMe, y el valor nuevo quedaría en una
 * consulta que nadie observa (la pantalla no se enteraría).
 */
export function resetSession(queryClient: QueryClient, me: Me | null): void {
  queryClient.setQueryData(ME_KEY, me);
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== ME_KEY[0] });
}

/** Usuario actual o null sin sesión (401). Cualquier otro error sube a la pantalla. */
export const meQuery = queryOptions({
  queryKey: ME_KEY,
  queryFn: async (): Promise<Me | null> => {
    try {
      return await unwrap(api.GET('/auth/me'));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
  staleTime: 60_000,
  retry: false,
});

export function useMe() {
  return useQuery(meQuery);
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/auth/logout')),
    onSettled: () => {
      resetSession(queryClient, null);
    },
  });
}

export function useSetActiveClinic() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clinicId: string) => unwrap(api.POST('/auth/active-clinic', { body: { clinicId } })),
    onSuccess: (me) => {
      resetSession(queryClient, me);
    },
  });
}

export const can = (me: Me | null | undefined, permissions: readonly string[]): boolean =>
  permissions.some((p) => me?.permissions?.includes(p) === true);
